import { isNil } from 'lodash'
import { SnackbarKey, useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { updateUser } from 'features/user'
import { GraphQlResponse } from 'providers/tanstack-query/response'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { fromDTO, toDTO } from './mapper'
import { UPDATE_CURRENT_WORKSPACE } from './mutation'
import {
  Data,
  Props,
  UpdateStatus,
  UseUpdateCurrentWorkspace,
  Variables,
  VariablesDTO,
} from './types'

export const useUpdateCurrentWorkspace = ({ userId }: Props): UseUpdateCurrentWorkspace => {
  const { t } = useTranslation('features/workspace')
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()

  const enqueueError = (): SnackbarKey =>
    enqueueSnackbar(t('updateCurrentWorkspace.error'), { variant: 'error' })

  const { mutateAsync } = useMutation<{ input: VariablesDTO }, Data>({
    mutation: UPDATE_CURRENT_WORKSPACE,
    onCompleted: async (data: Data) => {
      const {
        updateCurrentWorkspace: { errors, user },
      } = data

      if (errors && errors.length > 0) {
        enqueueError()
      } else if (user !== null) {
        const { currentWorkspace, currentWorkspaceRole, workspaceProducts } = fromDTO(user)

        dispatch(updateUser({ currentWorkspace, currentWorkspaceRole, workspaceProducts }))
      }
    },
    queriesToInvalidate: ['getWorkspaceProducts'],
  })

  const update = async (id?: Variables['currentWorkspaceId']): Promise<UpdateStatus> => {
    const result: GraphQlResponse<Data> | void = await mutateAsync({
      input: toDTO({
        currentWorkspaceId: id,
        id: userId,
      }),
    })

    if (result === undefined) {
      return 'failure'
    }

    const responseErrorsLength = result.errors?.length
    const mutationErrorsLength = result.data.updateCurrentWorkspace?.errors?.length

    if (isNil(responseErrorsLength) && isNil(mutationErrorsLength)) {
      return 'success'
    } else {
      return 'failure'
    }
  }

  return { update }
}
